import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const MuseMark = ({ className = "" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" fill="currentColor" />
  </svg>
);

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const navLinkClass = (path) =>
    `font-medium text-sm tracking-wide transition-colors duration-200 ${isActive(path) ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`;

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/method', label: 'The Muse Method' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/case-studies', label: 'Case Studies' },
    { to: '/about', label: 'About' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container-main">
        <nav className="flex items-center justify-between h-32">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src="/images/muse-logo.png" alt="Muse Social Creative Agency" className="h-48 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map(({ to, label }) => (
              <Link key={to} to={to} className={navLinkClass(to)} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                {label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a href="https://stan.store/MuseSocialCreative" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-foreground text-foreground font-medium text-sm px-6 py-2.5 rounded-full hover:bg-foreground hover:text-background transition-colors duration-300" data-testid="nav-shop-btn">
              Shop
            </a>
            <Link to="/book-a-call" className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-6 py-2.5 rounded-full hover:bg-butter-dark transition-colors duration-300" data-testid="nav-book-call-btn">
              Contact
            </Link>
          </div>

          <button className="lg:hidden p-2 text-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-background border-b border-border overflow-hidden">
            <div className="container-main py-6 flex flex-col gap-5">
              {navLinks.map(({ to, label }) => (
                <Link key={to} to={to} className={navLinkClass(to)} onClick={() => setIsMobileMenuOpen(false)} data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {label}
                </Link>
              ))}
              <a href="https://stan.store/MuseSocialCreative" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-foreground text-foreground font-medium text-sm px-6 py-2.5 rounded-full hover:bg-foreground hover:text-background transition-colors duration-300 mt-2" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-shop">
                Shop
              </a>
              <Link to="/book-a-call" className="inline-flex items-center justify-center gap-2 bg-butter text-foreground font-medium text-sm px-6 py-2.5 rounded-full hover:bg-butter-dark transition-colors duration-300 mt-2" onClick={() => setIsMobileMenuOpen(false)} data-testid="mobile-nav-contact">
                Contact
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
